import { For, Show, createMemo, createSignal } from "solid-js";

import { ScaiControlErrorReference } from "~/components/ScaiControlErrorReference";
import { useChangeSubscriptionPlan, usePlanCatalogue } from "~/lib/hooks";
import type { PlanCatalogueEntry, SubscriptionRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
  subscription: SubscriptionRead;
  onClose: () => void;
};

/**
 * Dialog for the CRM-driven plan change against ScaiControl.
 *
 * Flow:
 * 1. Fetch the cached plan catalogue (15-min backend TTL).
 * 2. Optionally filter by `service_slug` matching the current
 *    subscription's plan_key.
 * 3. Show the current plan + a dropdown of others. Reason field is
 *    optional metadata that rides along to ScaiControl.
 * 4. On submit, POST and surface the rep-facing error copy on failure.
 *    Local Subscription row catches up via webhook ~30s later — the
 *    success state tells the rep to expect that.
 */
export function ChangePlanDialog(props: Props) {
  const catalogue = usePlanCatalogue(props.wsKey, () => true);
  const mutate = useChangeSubscriptionPlan(props.wsKey);

  const [planId, setPlanId] = createSignal("");
  const [reason, setReason] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  // Keep the raw error around so ScaiControlErrorReference can pull
  // request_id + scaicontrol_code out of the ApiException details.
  const [errorObj, setErrorObj] = createSignal<unknown>(null);
  const [success, setSuccess] = createSignal<string | null>(null);

  // Plans filtered to the same service as the current sub — we can't
  // generally tell what `service_slug` the current sub maps to (the CRM
  // row carries `plan_key` which equals plan_slug from ScaiControl, but
  // not service_slug). Best-effort: filter on shared `service_slug`
  // when at least one catalogue entry shares the current plan's slug.
  const plans = createMemo<PlanCatalogueEntry[]>(() => {
    const all = catalogue.data?.plans ?? [];
    if (all.length === 0) return [];
    const currentSlug = props.subscription.plan_key;
    const current = all.find((p) => p.slug === currentSlug);
    if (current?.service_slug) {
      return all.filter((p) => p.service_slug === current.service_slug);
    }
    return all;
  });

  // Current plan id, if we can find it in the catalogue — used to grey
  // the row + default the dropdown.
  const currentPlanId = createMemo(() => {
    const current = (catalogue.data?.plans ?? []).find(
      (p) => p.slug === props.subscription.plan_key,
    );
    return current?.id ?? null;
  });

  const submit = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    setErrorObj(null);
    setSuccess(null);
    const chosen = planId();
    if (!chosen) {
      setError("Pick a plan first.");
      return;
    }
    if (chosen === currentPlanId()) {
      setError("That's the current plan — pick a different one.");
      return;
    }
    try {
      const result = await mutate.mutateAsync({
        subId: props.subscription.id,
        body: { plan_id: chosen, reason: reason().trim() || null },
      });
      setSuccess(result.note);
      // Auto-close after a short delay so the rep sees the confirmation.
      window.setTimeout(() => props.onClose(), 1500);
    } catch (err) {
      setError((err as Error).message);
      setErrorObj(err);
    }
  };

  return (
    <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="change-plan-title">
      <div class="modal-box max-w-xl">
        <h3 id="change-plan-title" class="font-bold text-lg mb-2">
          Change plan
        </h3>
        <p class="text-xs opacity-60 mb-4">
          Submits a plan change to ScaiControl. Your CRM will reflect the new state on its next
          webhook (~30s).
        </p>

        <div class="text-sm mb-3">
          <span class="opacity-60">Current plan:</span>{" "}
          <strong>{props.subscription.plan_name}</strong>{" "}
          <code class="text-xs opacity-60">{props.subscription.plan_key}</code>
        </div>

        <Show when={catalogue.isLoading}>
          <p class="text-sm opacity-60">Loading plan catalogue…</p>
        </Show>

        <Show when={catalogue.isError}>
          <div role="alert" class="alert alert-error text-sm">
            Couldn't load plans. ScaiControl may be unavailable.
          </div>
        </Show>

        <Show when={catalogue.data && plans().length === 0}>
          <div role="alert" class="alert alert-warning text-sm">
            No plans in the catalogue for this service.
          </div>
        </Show>

        <Show when={catalogue.data && plans().length > 0}>
          <form onSubmit={submit} class="flex flex-col gap-3">
            <div class="form-control">
              <label class="label" for="change-plan-select">
                <span class="label-text">New plan</span>
              </label>
              <select
                id="change-plan-select"
                class="select select-bordered select-sm"
                value={planId()}
                onChange={(e) => setPlanId(e.currentTarget.value)}
                disabled={mutate.isPending || success() !== null}
              >
                <option value="">— pick a plan —</option>
                <For each={plans()}>
                  {(p) => (
                    <option value={p.id} disabled={p.id === currentPlanId()}>
                      {planLabel(p)}
                      {p.id === currentPlanId() ? " · current" : ""}
                    </option>
                  )}
                </For>
              </select>
            </div>

            <div class="form-control">
              <label class="label" for="change-plan-reason">
                <span class="label-text">Reason (optional)</span>
              </label>
              <input
                id="change-plan-reason"
                type="text"
                class="input input-bordered input-sm"
                placeholder="e.g. Deal won — upgrade to Pro"
                value={reason()}
                onInput={(e) => setReason(e.currentTarget.value)}
                disabled={mutate.isPending || success() !== null}
                maxLength={500}
              />
            </div>

            <Show when={error()}>
              <div role="alert" class="alert alert-error text-sm flex flex-col items-start">
                <span>{error()}</span>
                <ScaiControlErrorReference error={errorObj()} />
              </div>
            </Show>

            <Show when={success()}>
              <div role="alert" class="alert alert-success text-sm">
                {success()}
              </div>
            </Show>

            <div class="modal-action">
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                onClick={props.onClose}
                disabled={mutate.isPending}
              >
                {success() ? "Close" : "Cancel"}
              </button>
              <Show when={!success()}>
                <button
                  type="submit"
                  class="btn btn-primary btn-sm"
                  disabled={mutate.isPending || !planId()}
                >
                  {mutate.isPending ? "Submitting…" : "Submit"}
                </button>
              </Show>
            </div>
          </form>
        </Show>
      </div>
      <button type="button" class="modal-backdrop" aria-label="Close" onClick={props.onClose} />
    </dialog>
  );
}

function planLabel(p: PlanCatalogueEntry): string {
  const name = p.name ?? p.slug ?? p.id;
  const tier = p.tier ? ` · ${p.tier}` : "";
  if (p.base_price_cents != null && p.currency) {
    const price = (p.base_price_cents / 100).toFixed(0);
    const period = p.billing_period === "yearly" ? "/yr" : "/mo";
    return `${name}${tier} · ${price} ${p.currency}${period}`;
  }
  return `${name}${tier}`;
}
